import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type UseDeleteJobLevelsParams = {
    readonly jobLevels: NonEmptyArray<JobLevel | JobLevelDetails>;
};

type DeleteJobLevelsContext = {
    readonly architectureId: string;
};

export type UseDeleteJobLevelsResult = UseMutationResult<
    unknown,
    Error,
    UseDeleteJobLevelsParams,
    DeleteJobLevelsContext
>;

export const useDeleteJobLevels = (): UseDeleteJobLevelsResult => {
    const { deleteJobLevels } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobLevels }: UseDeleteJobLevelsParams) => {
            const result = await deleteJobLevels(jobLevels);

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params): DeleteJobLevelsContext => {
            const { architectureId } = params.jobLevels[0];
            const levelsByRole = new Map<string, (JobLevel | JobLevelDetails)[]>();

            params.jobLevels.forEach((level) => {
                if (level.parentId !== null) {
                    const roleId = level.parentId;
                    if (!levelsByRole.has(roleId)) {
                        levelsByRole.set(roleId, []);
                    }
                    levelsByRole.get(roleId)!.push(level);
                }
            });

            levelsByRole.forEach((levelsToDelete, roleId) => {
                queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.levelsByRole(architectureId, roleId),
                });

                queryClient.setQueryData(
                    jobArchitectureKeys.levelsByRole(architectureId, roleId),
                    (old: readonly JobLevel[] | undefined) => {
                        return old?.filter((jobLevel) => !levelsToDelete.includes(jobLevel));
                    },
                );
            });

            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.archivedLevels(architectureId),
            });

            const deletedIds = new Set(params.jobLevels.map((level) => level.id));
            queryClient.setQueryData(
                jobArchitectureKeys.archivedLevels(architectureId),
                (old: readonly JobLevel[] | undefined) => old?.filter((level) => !deletedIds.has(level.id)),
            );

            return { architectureId };
        },
        onSuccess: (_, __, context) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architecture(context.architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectureDetail(context.architectureId),
            });
        },
        throwOnError: false,
    });
};
